import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { User, Settings, LogOut, ShoppingBag, Heart, Package } from 'lucide-react';
import { getMyOrders } from '../services/api';

const UserProfileDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [orderItemsCount, setOrderItemsCount] = useState(0);
  const dropdownRef = useRef(null);
  const { user, logout, getToken } = useAuth();
  const { getTotalItems } = useCart();
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    setIsOpen(false);
    navigate('/');
  };

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const loadOrdersCount = async () => {
      try {
        const token = getToken?.();
        if (!token) {
          if (!cancelled) setOrderItemsCount(0);
          return;
        }
        const orders = await getMyOrders(token);
        const list = Array.isArray(orders) ? orders : (orders?.orders || []);
        const count = list.reduce((sum, o) => {
          const its = Array.isArray(o?.items) ? o.items : [];
          const qty = its.reduce((s, it) => s + (Number(it?.quantity) || 1), 0);
          return sum + qty;
        }, 0);
        if (!cancelled) setOrderItemsCount(count);
      } catch (_) {
        if (!cancelled) setOrderItemsCount(0);
      }
    };

    loadOrdersCount();
    return () => {
      cancelled = true;
    };
  }, [isOpen, getToken]);

  const getUserInitials = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
    }
    if (user?.name) {
      return user.name.charAt(0).toUpperCase();
    }
    return user?.email?.charAt(0).toUpperCase() || 'U';
  };

  const getUserDisplayName = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    if (user?.name) {
      return user.name;
    }
    return user?.email || 'User';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Profile Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 text-gray-700 hover:text-orange-500 transition-colors group"
      >
        {/* Avatar */}
        <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center font-medium text-sm group-hover:bg-orange-600 transition-colors">
          {getUserInitials()}
        </div>
        
        {/* Name */}
        <span className="hidden md:inline font-medium max-w-32 truncate">
          {getUserDisplayName()}
        </span>
        
        {/* Dropdown Arrow */}
        <svg 
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 animate-in fade-in-0 zoom-in-95">
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-orange-500 text-white rounded-full flex items-center justify-center font-medium">
                {getUserInitials()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {getUserDisplayName()}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user?.email}
                </p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            <Link
              to="/profile"
              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <User className="w-4 h-4 mr-3" />
              My Profile
            </Link>
            
            <Link
              to="/orders"
              className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <span className="flex items-center">
                <Package className="w-4 h-4 mr-3" />
                My Orders
              </span>
              {orderItemsCount > 0 && (
                <span className="ml-3 inline-flex items-center justify-center min-w-6 h-6 px-2 rounded-full bg-blue-600 text-white text-xs font-semibold">
                  {orderItemsCount}
                </span>
              )}
            </Link>
            
            
            
            <Link
              to="/cart"
              className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <span className="flex items-center">
                <ShoppingBag className="w-4 h-4 mr-3" />
                Shopping Cart
              </span>
              {getTotalItems() > 0 && (
                <span className="ml-3 inline-flex items-center justify-center min-w-6 h-6 px-2 rounded-full bg-orange-500 text-white text-xs font-semibold">
                  {getTotalItems()}
                </span>
              )}
            </Link>
            
            <Link
              to="/usersettings"
              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <Settings className="w-4 h-4 mr-3" />
              Settings
            </Link>
          </div>

          {/* Logout Button */}
          <div className="border-t border-gray-100 pt-1">
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileDropdown;
